import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import { css } from '@emotion/core'
import { fetchDiscount, fetchReservationDiscount, fetchSales, fetchSalesReservation, fetchSmaregiTransactionDetail } from '@/apis/aipass'
import { SalesDiscountType, SalesType } from '@/models/sales'
import { TransactionSaleType, TransactionSideType, TransactionType } from '@/models/smaregi/transaction'
import { groupBy } from 'lodash'
import { LoaderContextCreator } from '@/contexts/loader'

type Props = {
  reservationId?: string
  checkinId?: string
  transaction?: TransactionType
  filterGuestRoomAssignId?: string[]
  onChange: (selectedSaleIds: string[], amount: number) => void
}

type SaleItemType = {
  id: string
  isSelected: boolean
  roomNumber: string | undefined
  guestRoomAssignId: string | undefined
  guestRoomAssignShortId: string | undefined
  date: Dayjs
  subject: string
  subSubject: string | undefined
  quantity: number
  price: number
  transactionSide: TransactionSideType | undefined
  createdAt: Dayjs | undefined
  salesSubjectCode: string | undefined
  isDiscount: boolean
}

export const AdjustmentSalesSelectList: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())

  const [saleItemList, setSaleItemList] = useState<SaleItemType[]>([])
  const [sumSelectSalePrice, setSumSelectSalePrice] = useState<number>(0)

  const transactionSalesToSaleItem = ({
    transactionSide,
    sales,
  }: {
    transactionSide: TransactionSideType
    sales?: TransactionSaleType[]
  }): SaleItemType[] => {
    return (sales || [])
      .filter(sale => !sale.isPaid)
      .map(sale => ({
        id: sale.salesId,
        isSelected: transactionSide !== TransactionSideType.Smaregi && sale.quantity !== 0,
        roomNumber: undefined,
        guestRoomAssignId: undefined,
        guestRoomAssignShortId: undefined,
        date: dayjs(sale.salesAt),
        subject: sale.saleSubjectName,
        subSubject: sale.saleSubSubjectName,
        quantity: sale.quantity,
        price: sale.salesPrice,
        transactionSide,
        createdAt: undefined,
        salesSubjectCode: undefined,
        isDiscount: sale.isDiscount,
      }))
  }

  const salesToSaleItem = ({ sales }: { sales: SalesType[] }): SaleItemType[] => {
    return sales
      .filter(sale => !sale.isPaid && sale.id)
      .map(sale => ({
        id: sale.id || '',
        isSelected: sale.transactionSide !== TransactionSideType.Smaregi && sale.quantity !== 0,
        roomNumber: sale.roomNumber,
        guestRoomAssignId: sale.guestRoomAssignId,
        guestRoomAssignShortId: sale.guestRoomAssignShortId,
        date: dayjs(sale.salesDate),
        subject: sale.salesSubjectName,
        subSubject: sale.salesSubSubjectName || undefined,
        quantity: sale.quantity,
        price: sale.salesPrice,
        transactionSide: sale.transactionSide,
        createdAt: dayjs(sale.createdAt),
        salesSubjectCode: sale.salesSubjectCode,
        isDiscount: false,
      }))
  }

  const discountToSaleItem = ({ sales }: { sales: SalesDiscountType[] }): SaleItemType[] => {
    return sales
      .filter(sale => sale.salesPaymentName === '-')
      .map(sale => ({
        id: sale.id,
        isSelected: sale.transactionSide !== TransactionSideType.Smaregi,
        roomNumber: sale.roomNumber,
        guestRoomAssignId: sale.guestRoomAssignId,
        guestRoomAssignShortId: sale.guestRoomAssignShortId,
        date: dayjs(sale.salesDate),
        subject: sale.salesDiscountName,
        subSubject: undefined,
        quantity: 1,
        price: sale.salesSubjectPrice * -1,
        transactionSide: sale.transactionSide,
        createdAt: dayjs(sale.createdAt),
        salesSubjectCode: sale.salesSubjectCode,
        isDiscount: true,
      }))
  }

  const fetchSalesByCheckinId = (checkinId: string) => {
    setIsLoading(true)
    Promise.all([fetchSales(checkinId).then(salesToSaleItem), fetchDiscount(checkinId).then(discountToSaleItem)])
      .then(fetchedSales => {
        let salesAndDiscount: Array<SaleItemType> = []
        if (props.filterGuestRoomAssignId !== undefined) {
          salesAndDiscount = fetchedSales.flat().filter(sale => props.filterGuestRoomAssignId!.includes(sale.guestRoomAssignId || ''))
        } else {
          salesAndDiscount = fetchedSales.flat()
        }
        setSaleItemList(Object.values(groupBy(salesAndDiscount, s => s.guestRoomAssignId)).flatMap(g => g))
      })
      .finally(() => setIsLoading(false))
  }

  const fetchSalesByReservation = (reservationId: string) => {
    setIsLoading(true)
    Promise.all([
      fetchSalesReservation(reservationId).then(salesToSaleItem),
      fetchReservationDiscount(reservationId).then(discountToSaleItem),
    ])
      .then(fetchedSales => {
        let salesAndDiscount: Array<SaleItemType> = []
        if (props.filterGuestRoomAssignId !== undefined) {
          salesAndDiscount = fetchedSales.flat().filter(sale => props.filterGuestRoomAssignId!.includes(sale.guestRoomAssignId || ''))
        } else {
          salesAndDiscount = fetchedSales.flat()
        }
        setSaleItemList(Object.values(groupBy(salesAndDiscount, s => s.guestRoomAssignId)).flatMap(g => g))
      })
      .finally(() => setIsLoading(false))
  }

  const fetchSalesByTransaction = (transaction: TransactionType) => {
    setIsLoading(true)
    fetchSmaregiTransactionDetail(transaction.id)
      .then((transaction: TransactionType) => {
        const sales = transactionSalesToSaleItem(transaction)
        setSaleItemList(sales)
      })
      .finally(() => setIsLoading(false))
  }

  const isSubmittedItem = (saleItem: SaleItemType) => {
    return saleItem.transactionSide === TransactionSideType.Smaregi || saleItem.quantity === 0
  }

  const checkedSaleItemList = (index: number) => {
    setSaleItemList(
      saleItemList.map((item, i) => {
        if (i !== index) {
          return item
        }
        item.isSelected = !item.isSelected
        return item
      }),
    )
  }

  const selectableItems = saleItemList.filter(item => !isSubmittedItem(item))
  const isAllSelected = selectableItems.length > 0 && selectableItems.every(item => item.isSelected)

  const toggleSelectAll = () => {
    const hasNoSelectable = selectableItems.length === 0
    if (hasNoSelectable) return
    setSaleItemList(
      saleItemList.map(item => {
        if (isSubmittedItem(item)) return item
        return { ...item, isSelected: !isAllSelected }
      }),
    )
  }

  useEffect(() => {
    const selectedSales = saleItemList.filter(item => item.isSelected)
    const sumSelectedPrice = selectedSales.reduce((carry, item) => carry + item.price, 0)
    props.onChange(
      selectedSales.map(item => item.id),
      sumSelectedPrice,
    )
    setSumSelectSalePrice(sumSelectedPrice)
  }, [saleItemList])

  useEffect(() => {
    if (props.checkinId) {
      fetchSalesByCheckinId(props.checkinId)
    } else if (props.reservationId) {
      fetchSalesByReservation(props.reservationId)
    } else if (props.transaction) {
      fetchSalesByTransaction(props.transaction)
    }
  }, [])

  return (
    <div css={contentSalesListAreaStyle}>
      <div css={contentSalesListHeaderStyle}>
        <div css={contentSaleListCheckColStyle}>
          <input
            width={18}
            alt="checkbox"
            type="image"
            name="selectAll"
            disabled={saleItemList.every(item => isSubmittedItem(item))}
            src={require(`@/static/images/check-box${isAllSelected ? '' : '_off'}.svg`)}
            onClick={e => {
              e.stopPropagation()
              toggleSelectAll()
            }}
          />
        </div>
        <div css={contentSaleListReservationColStyle}>
          {t('Room number')}
          <br />({t('Room ID')})
        </div>
        <div css={contentSaleListSaleDateColStyle}>{t('Date of use')}</div>
        <div css={contentSaleListSubjectColStyle}>{t('Reference')}</div>
        <div css={contentSaleListSubSubjectColStyle}>{t('Supplementary subjects')}</div>
        <div css={contentSaleListQuantityColStyle}>{t('Quantity')}</div>
        <div css={contentSaleListPriceColStyle}>{t('Amount used')}</div>
      </div>
      <div css={contentSalesListBodyStyle}>
        {saleItemList.map((saleItem, idx) => (
          <div
            css={!isSubmittedItem(saleItem) ? contentSalesListRowStyle : contentDisabledSalesListRowStyle}
            key={idx}
            onClick={() => !isSubmittedItem(saleItem) && checkedSaleItemList(idx)}
          >
            <div css={contentSaleListCheckColStyle}>
              <input
                width={18}
                alt="checkbox"
                type="image"
                name="cancellationReport"
                disabled={isSubmittedItem(saleItem)}
                src={require(`@/static/images/check-box${saleItem.isSelected ? '' : '_off'}.svg`)}
              />
            </div>
            <div css={contentSaleListReservationColStyle}>
              {saleItem.roomNumber ? (
                <>
                  {saleItem.roomNumber}
                  <br />({saleItem.guestRoomAssignShortId})
                </>
              ) : (
                '-'
              )}
            </div>
            <div css={contentSaleListSaleDateColStyle}>{saleItem.date.format(t('MM-DD'))}</div>
            <div css={contentSaleListSubjectColStyle}>{saleItem.subject}</div>
            <div css={contentSaleListSubSubjectColStyle}>{saleItem.subSubject || '-'}</div>
            <div css={contentSaleListQuantityColStyle}>{saleItem.quantity}</div>
            <div css={contentSaleListPriceColStyle}>¥{saleItem.price.toLocaleString()}</div>
          </div>
        ))}
      </div>
      <div css={contentSalesListFooterStyle}>
        <span style={{ fontSize: 14, fontWeight: 'bold', letterSpacing: 0.7 }}>{t('Amount of money')}</span>
        <span style={{ fontSize: 20, fontWeight: 'bold', width: 140, textAlign: 'end' }}>¥{sumSelectSalePrice.toLocaleString()}</span>
      </div>
    </div>
  )
}

const contentSalesListAreaStyle = css({
  height: 472,
  border: '1px solid #CCCCCC',
  borderRadius: 10,
  display: 'flex',
  flexFlow: 'column',
  overflow: 'hidden',
})
const contentSalesListHeaderStyle = css({
  padding: '10px 35px 10px 24px',
  display: 'flex',
  fontSize: 12,
  letterSpacing: 1.2,
  fontWeight: 'bold',
  backgroundColor: '#f2f2f2',
  borderRadius: '5px 5px 0 0',
})
const contentSalesListBodyStyle = css({
  backgroundColor: '#ffffff',
  height: '100%',
  overflow: 'hidden auto',
})
const contentSalesListFooterStyle = css({
  display: 'flex',
  justifyContent: 'end',
  alignItems: 'center',
  padding: '20px 35px',
  backgroundColor: '#ffffff',
  borderRadius: '0 0 10px 10px',
  borderTop: '1px solid #CCCCCC',
})
const contentSalesListRowStyle = css({
  cursor: 'pointer',
  padding: '14px 35px 14px 24px',
  display: 'flex',
  alignItems: 'center',
  fontSize: 14,
  letterSpacing: 1.4,
  color: '#272727',
  borderBottom: '1px solid #f2f2f2',
})
const contentDisabledSalesListRowStyle = css(contentSalesListRowStyle, {
  cursor: 'auto',
  backgroundColor: '#f5f5f5',
  div: {
    color: '#A3A3A3',
  },
})
const contentSaleListCheckColStyle = css({ width: 40 })
const contentSaleListReservationColStyle = css({ width: 80 })
const contentSaleListSaleDateColStyle = css({ width: 100 })
const contentSaleListSubjectColStyle = css({ flexGrow: 1 })
const contentSaleListSubSubjectColStyle = css({ flexGrow: 1, width: 200, maxWidth: 200 })
const contentSaleListQuantityColStyle = css({ width: 60 })
const contentSaleListPriceColStyle = css({ width: 80, textAlign: 'end' })
